import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
  id: string
  number: number
  icon: string
  title: string
  description: string
  detail: string
}

const processSteps: ProcessStep[] = [
  {
    id: 'step-1',
    number: 1,
    icon: 'ArrowDownTrayIcon',
    title: 'Water stroomt in',
    description: 'Hard water met opgelost calciumbicarbonaat stroomt de unit binnen',
    detail: 'Het water bevat alle natuurlijke mineralen zoals calcium en magnesium.',
  },
  {
    id: 'step-2',
    number: 2,
    icon: 'CogIcon',
    title: 'Katalytische transformatie',
    description:
      'De spiraalvormige legeringskern zet calciet (hecht) om in aragoniet (hecht niet)',
    detail:
      'Puur fysisch proces zonder toevoegingen of chemicaliën. Wetenschappelijk gevalideerd.',
  },
  {
    id: 'step-3',
    number: 3,
    icon: 'CheckCircleIcon',
    title: 'Behandeld water uit',
    description: 'Alle mineralen behouden. Kalk hecht niet meer. Bestaande aanslag wordt afgebroken',
    detail: 'Water blijft tot 3 maanden behandeld — versus 24-48 uur bij magnetische systemen.',
  },
]

export default function HowItWorksSection() {
  return (
    <section id="hoe-werkt-het" className="py-20 md:py-32 relative overflow-hidden">
      {/* Subtle radial accent at section transition */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#0584F2]/05 via-transparent to-transparent pointer-events-none"></div>
      <div className="mx-auto max-w-7xl px-6">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16 reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5">
            <Icon name="LightBulbIcon" size={16} className="text-primary" />
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              Hoe werkt het?
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold" style={{ color: '#0F1E2E' }}>
            Wetenschappelijk bewezen proces
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#2F4F6E' }}>
            Katalytische technologie die calciet transformeert naar aragoniet — zonder chemicaliën
            of elektriciteit.
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {processSteps.map((step, index) => (
            <div key={step.id} className="step-card overflow-visible">
              <div className="bg-white rounded-card p-8 border border-[#0584F2]/15 h-full flex flex-col card-3d relative shadow-md">
                {/* Step Number Badge */}
                <div className="flex items-start justify-between mb-6">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon name={step.icon as any} size={24} className="text-primary" />
                  </div>
                  <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                    <span className="text-lg font-heading font-bold text-primary-foreground">
                      {step.number}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 space-y-4">
                  <h3 className="text-xl font-heading font-bold" style={{ color: '#0F1E2E' }}>{step.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#2F4F6E' }}>{step.description}</p>
                  <div className="pt-4 border-t border-[#0584F2]/10">
                    <p className="text-xs italic" style={{ color: '#2F4F6E' }}>{step.detail}</p>
                  </div>
                </div>

                {/* Connecting Arrow (not on last card) */}
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <Icon name="ArrowRightIcon" size={24} className="text-primary" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Key Fact Highlight */}
        <div className="bg-white rounded-card p-8 md:p-12 border-2 border-primary shadow-md reveal card-3d">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Icon name="ClockIcon" size={32} className="text-primary" />
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-heading font-bold" style={{ color: '#0F1E2E' }}>
                  Tot 3 maanden behandeld
                </p>
                <p className="text-sm" style={{ color: '#2F4F6E' }}>
                  versus 24-48 uur bij magnetische systemen
                </p>
              </div>
            </div>
            <div className="shrink-0">
              <div className="px-6 py-3 rounded-full bg-success text-success-foreground text-sm font-bold uppercase tracking-wider">
                Langdurig effect
              </div>
            </div>
          </div>
        </div>

        {/* Scientific Validation */}
        <div className="mt-16 text-center reveal">
          <p className="text-sm font-medium uppercase tracking-wider mb-6" style={{ color: '#2F4F6E' }}>
            Wetenschappelijk gevalideerd door
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm font-medium" style={{ color: '#2F4F6E' }}>
            <span>University of Reading</span>
            <span>·</span>
            <span>Texas A&M</span>
            <span>·</span>
            <span>Brunel University</span>
            <span>·</span>
            <span>Dublin Institute</span>
            <span>·</span>
            <span>Wroclaw University</span>
            <span>·</span>
            <span>Harwell Science</span>
          </div>
        </div>
      </div>
    </section>
  )
}